<!-- Loading State -->
<div *ngIf="isLoading" class="d-flex justify-content-center align-items-center" style="min-height: 400px;">
  <div class="spinner-border text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
</div>

<!-- Vehicle Detail Content -->
<div *ngIf="!isLoading && vehicleData" class="vehicle-detail-container">

  <!-- Header Section -->
  <div class="card mb-4">
    <div class="card-body">
      <div class="d-flex justify-content-between align-items-start mb-4">
        <div>
          <button type="button" class="btn btn-light btn-sm mb-3" (click)="goBack()">
            <i class="ki-duotone ki-arrow-left fs-2">
              <span class="path1"></span>
              <span class="path2"></span>
            </i>
            Back to Search
          </button>
          <h1 class="fw-bold text-gray-900 mb-2">
            {{ vehicleData.company_name }} {{ vehicleData.name }} {{ vehicleData.year }}
          </h1>
          <div class="d-flex align-items-center gap-4 text-muted">
            <span><i class="ki-duotone ki-calendar fs-3 me-1"><span class="path1"></span><span
                  class="path2"></span></i>Lot: {{ vehicleData.lot_no }}</span>
            <span><i class="ki-duotone ki-car fs-3 me-1"><span class="path1"></span><span class="path2"></span><span
                  class="path3"></span></i>{{ vehicleData.inventory_no }}</span>
            <span class="badge" [class]="vehicleData.is_sold ? 'badge-danger' : 'badge-success'">
              {{ vehicleData.is_sold ? 'SOLD' : 'AVAILABLE' }}
            </span>
          </div>
        </div>
        <div class="text-end">
          <div class="fs-2 fw-bold text-primary mb-1">¥{{ formatPrice(vehicleData.price) }}</div>
          <div class="text-muted">Current Price</div>
        </div>
      </div>

      <!-- Auction Info Bar -->
      <div class="auction-info-bar p-3 bg-light-primary rounded">
        <div class="row align-items-center">
          <div class="col-md-8">
            <div class="d-flex align-items-center">
              <i class="ki-duotone ki-time fs-1 text-primary me-3">
                <span class="path1"></span>
                <span class="path2"></span>
              </i>
              <div>
                <div class="fw-bold text-gray-900">Auction Date</div>
                <div class="text-primary">{{ formatAuctionDate(vehicleData.auction_date) }}</div>
              </div>
            </div>
          </div>
          <div class="col-md-4 text-end">


            <div class="fw-bold text-gray-700 mb-1">
              <i class="ki-duotone ki-home fs-1 text-primary me-3">
                <span class="path1"></span>
                <span class="path2"></span>
              </i>
              Hall Name
            </div>
            <div class="text-muted small">{{ vehicleData?.hall_name || 'Auction Hall' }}</div>

          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Main Content Row -->
  <div class="row g-4">

    <!-- Left Column - Images and Details -->
    <div class="col-lg-8">

      <!-- Vehicle Information Table -->
      <div class="card mb-4">
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-bordered mb-0 vehicle-info-table">
              <tbody>
                <!-- First Row -->
                <tr>
                  <td class="info-label">Auction Date</td>
                  <td class="info-value">{{ formatAuctionDate(vehicleData.auction_date) }}</td>
                  <td class="info-label">Auction Time</td>
                  <td class="info-value">{{ formatAuctionTime(vehicleData.auction_date) }}</td>
                  <td class="info-label">Lot Number</td>
                  <td class="info-value">{{ vehicleData.lot_no }}</td>
                  <td class="info-label">Auction Hall</td>
                  <td class="info-value">{{ vehicleData?.hall_name || '-' }}</td>
                </tr>
                <!-- Second Row -->
                <tr>
                  <td class="info-label">Year</td>
                  <td class="info-value">{{ vehicleData.year }}</td>
                  <td class="info-label">Make</td>
                  <td class="info-value">{{ vehicleData.company_name }}</td>
                  <td class="info-label">Model</td>
                  <td class="info-value">{{ vehicleData.name }}</td>
                  <td class="info-label">Grade</td>
                  <td class="info-value">{{ vehicleData.parsed_data?.['Grade'] || '-' }}</td>
                </tr>
                <!-- Third Row -->
                <tr>
                  <td class="info-label">Engine</td>
                  <td class="info-value">{{ vehicleData.parsed_data?.['Engine Size'] || vehicleData.parsed_data?.['Engine'] || '-' }}</td>
                  <td class="info-label">Mileage</td>
                  <td class="info-value">{{ vehicleData.mileage | number }}{{ vehicleData.mileage ? ' KM' : 'N/A' }}</td>
                  <td class="info-label">Color</td>
                  <td class="info-value">{{ vehicleData.parsed_data?.['Exterior Color'] || vehicleData.parsed_data?.['Color'] || 'BLACK' }}</td>
                  <td class="info-label">Transmission</td>
                  <td class="info-value">{{ vehicleData.parsed_data?.['Transmission'] || '-' }}</td>
                </tr>
                <!-- Fourth Row -->
                <tr>
                  <td class="info-label">Chassis No.</td>
                  <td class="info-value" colspan="3">{{ vehicleData.vin_no || '-' }}</td>
                  <td class="info-label">Current Price</td>
                  <td class="info-value price-highlight" colspan="3">
                    <span class="fw-bold text-primary fs-5">¥{{ formatPrice(vehicleData.price) }}</span>
                    <span class="badge badge-light-success ms-2" *ngIf="!vehicleData.is_sold">Available</span>
                    <span class="badge badge-danger ms-2" *ngIf="vehicleData.is_sold">SOLD</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <!-- Image Gallery -->
      <div class="card mb-4">
        <div class="card-body p-0">
          <!-- Main Image with Navigation -->
          <div class="image-gallery-wrapper">
            <!-- Left Navigation Arrow -->
            <button *ngIf="vehicleData.pictures && vehicleData.pictures.length > 1" type="button"
              class="btn btn-light btn-sm nav-arrow-btn nav-arrow-left"
              (click)="previousImage()" [disabled]="selectedImageIndex === 0"
              title="Previous Image">
              <span class="arrow-icon">‹</span>
            </button>

            <!-- Main Image Container -->
            <div class="main-image-container position-relative">
              <img [src]="getMainImage()" [alt]="vehicleData.company_name + ' ' + vehicleData.name"
                class="main-image w-100">

              <!-- Image Counter -->
              <div class="position-absolute bottom-0 end-0 bg-dark bg-opacity-75 text-white px-3 py-1 m-3 rounded">
                {{ selectedImageIndex + 1 }} / {{ vehicleData.pictures?.length || 1 }}
              </div>
            </div>

            <!-- Right Navigation Arrow -->
            <button *ngIf="vehicleData.pictures && vehicleData.pictures.length > 1" type="button"
              class="btn btn-light btn-sm nav-arrow-btn nav-arrow-right"
              (click)="nextImage()"
              [disabled]="selectedImageIndex === vehicleData.pictures.length - 1"
              title="Next Image">
              <span class="arrow-icon">›</span>
            </button>
          </div>

          <!-- Thumbnail Gallery -->
          <div *ngIf="vehicleData.pictures && vehicleData.pictures.length > 1" class="thumbnail-gallery p-3">
            <div class="d-flex gap-2 overflow-auto">
              <img *ngFor="let image of vehicleData.pictures; let i = index" [src]="image" [alt]="'Image ' + (i + 1)"
                class="thumbnail" [class.active]="i === selectedImageIndex" (click)="selectImage(i)"
                style="width: 80px; height: 60px; object-fit: cover; cursor: pointer; border-radius: 4px;">
            </div>
          </div>
        </div>
      </div>

      <!-- Vehicle Details Tabs -->
      <div class="card">
        <div class="card-header border-0 pt-6">
          <ul class="nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bold">
            <li class="nav-item">
              <a class="nav-link text-active-primary ms-0 me-10 py-3" [class.active]="activeTab === 'overview'"
                (click)="setActiveTab('overview')" role="button">
                Overview
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link text-active-primary ms-0 me-10 py-3" [class.active]="activeTab === 'specifications'"
                (click)="setActiveTab('specifications')" role="button">
                Specifications
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link text-active-primary ms-0 me-10 py-3" [class.active]="activeTab === 'features'"
                (click)="setActiveTab('features')" role="button">
                Features
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link text-active-primary ms-0 me-10 py-3" [class.active]="activeTab === 'inspection'"
                (click)="setActiveTab('inspection')" role="button">
                Inspection
              </a>
            </li>
          </ul>
        </div>

        <div class="card-body">
          <!-- Overview Tab -->
          <div *ngIf="activeTab === 'overview'" class="tab-content">
            <div class="row g-4">
              <div class="col-md-6">
                <div class="info-group">
                  <h6 class="fw-bold text-gray-800 mb-3">Basic Information</h6>
                  <div class="info-item">
                    <span class="label">Make:</span>
                    <span class="value">{{ vehicleData.company_name }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Model:</span>
                    <span class="value">{{ vehicleData.name }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Year:</span>
                    <span class="value">{{ vehicleData.year }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Color:</span>
                    <span class="value">{{ vehicleData.color }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Mileage:</span>
                    <span class="value">{{ formatPrice(vehicleData.mileage) }} km</span>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="info-group">
                  <h6 class="fw-bold text-gray-800 mb-3">Engine & Performance</h6>
                  <div class="info-item">
                    <span class="label">Engine:</span>
                    <span class="value">{{ vehicleData.engine_capacity }}cc</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Fuel Type:</span>
                    <span class="value">{{ vehicleData.fuel_type }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Transmission:</span>
                    <span class="value">{{ vehicleData.transmission }}</span>
                  </div>
                  <div class="info-item" *ngIf="vehicleData.doors">
                    <span class="label">Doors:</span>
                    <span class="value">{{ vehicleData.doors }}</span>
                  </div>
                  <div class="info-item" *ngIf="vehicleData.vin_no">
                    <span class="label">VIN:</span>
                    <span class="value">{{ vehicleData.vin_no }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Additional Info -->
            <div class="row g-4 mt-2" *ngIf="vehicleData.parsed_data">
              <div class="col-12">
                <div class="info-group">
                  <h6 class="fw-bold text-gray-800 mb-3">Additional Information</h6>
                  <div class="row">
                    <div class="col-md-6" *ngIf="vehicleData.parsed_data[' car history ']">
                      <div class="info-item">
                        <span class="label">Car History:</span>
                        <span class="value">{{ vehicleData.parsed_data[' car history '] }}</span>
                      </div>
                    </div>
                    <div class="col-md-6" *ngIf="vehicleData.parsed_data[' condition ']">
                      <div class="info-item">
                        <span class="label">Condition:</span>
                        <span class="value">{{ vehicleData.parsed_data[' condition '] }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Specifications Tab -->
          <div *ngIf="activeTab === 'specifications'" class="tab-content">
            <div class="row g-4">
              <div class="col-md-4" *ngIf="vehicleData.car_length || vehicleData.car_width || vehicleData.car_height">
                <div class="info-group">
                  <h6 class="fw-bold text-gray-800 mb-3">Dimensions</h6>
                  <div class="info-item" *ngIf="vehicleData.car_length">
                    <span class="label">Length:</span>
                    <span class="value">{{ vehicleData.car_length }} mm</span>
                  </div>
                  <div class="info-item" *ngIf="vehicleData.car_width">
                    <span class="label">Width:</span>
                    <span class="value">{{ vehicleData.car_width }} mm</span>
                  </div>
                  <div class="info-item" *ngIf="vehicleData.car_height">
                    <span class="label">Height:</span>
                    <span class="value">{{ vehicleData.car_height }} mm</span>
                  </div>
                </div>
              </div>

              <div class="col-md-4">
                <div class="info-group">
                  <h6 class="fw-bold text-gray-800 mb-3">Engine Details</h6>
                  <div class="info-item">
                    <span class="label">Engine Capacity:</span>
                    <span class="value">{{ vehicleData.engine_capacity }}cc</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Fuel Type:</span>
                    <span class="value">{{ vehicleData.fuel_type }}</span>
                  </div>
                  <div class="info-item" *ngIf="vehicleData.parsed_data[' fuel ']">
                    <span class="label">Fuel System:</span>
                    <span class="value">{{ vehicleData.parsed_data[' fuel '] }}</span>
                  </div>
                </div>
              </div>

              <div class="col-md-4" *ngIf="vehicleData.inspection_expiry_date">
                <div class="info-group">
                  <h6 class="fw-bold text-gray-800 mb-3">Inspection</h6>
                  <div class="info-item">
                    <span class="label">Expiry Date:</span>
                    <span class="value">{{ vehicleData.inspection_expiry_date | date:'mediumDate' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Features Tab -->
          <div *ngIf="activeTab === 'features'" class="tab-content">
            <div class="row g-4">
              <!-- Standard Features -->
              <div class="col-md-6">
                <div class="info-group">
                  <h6 class="fw-bold text-gray-800 mb-3">Standard Features</h6>
                  <div class="features-grid">
                    <div class="feature-item" [class.available]="vehicleData.airbag">
                      <i class="ki-duotone ki-shield-tick fs-2 me-2" [class.text-success]="vehicleData.airbag"
                        [class.text-muted]="!vehicleData.airbag">
                        <span class="path1"></span>
                        <span class="path2"></span>
                      </i>
                      <span>Airbag</span>
                    </div>
                    <div class="feature-item" [class.available]="vehicleData.air_condition">
                      <i class="ki-duotone ki-weather fs-2 me-2" [class.text-success]="vehicleData.air_condition"
                        [class.text-muted]="!vehicleData.air_condition">
                        <span class="path1"></span>
                        <span class="path2"></span>
                      </i>
                      <span>Air Conditioning</span>
                    </div>
                    <div class="feature-item" [class.available]="vehicleData.sunroof">
                      <i class="ki-duotone ki-sun fs-2 me-2" [class.text-success]="vehicleData.sunroof"
                        [class.text-muted]="!vehicleData.sunroof">
                        <span class="path1"></span>
                        <span class="path2"></span>
                      </i>
                      <span>Sunroof</span>
                    </div>
                    <div class="feature-item" [class.available]="vehicleData.leather_seat">
                      <i class="ki-duotone ki-crown fs-2 me-2" [class.text-success]="vehicleData.leather_seat"
                        [class.text-muted]="!vehicleData.leather_seat">
                        <span class="path1"></span>
                        <span class="path2"></span>
                      </i>
                      <span>Leather Seats</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Technology Features -->
              <div class="col-md-6">
                <div class="info-group">
                  <h6 class="fw-bold text-gray-800 mb-3">Technology Features</h6>
                  <div class="features-grid">
                    <div class="feature-item" [class.available]="vehicleData.tv">
                      <i class="ki-duotone ki-screen fs-2 me-2" [class.text-success]="vehicleData.tv"
                        [class.text-muted]="!vehicleData.tv">
                        <span class="path1"></span>
                        <span class="path2"></span>
                      </i>
                      <span>TV/Navigation</span>
                    </div>
                    <div class="feature-item" [class.available]="vehicleData.keyless">
                      <i class="ki-duotone ki-key fs-2 me-2" [class.text-success]="vehicleData.keyless"
                        [class.text-muted]="!vehicleData.keyless">
                        <span class="path1"></span>
                        <span class="path2"></span>
                      </i>
                      <span>Keyless Entry</span>
                    </div>
                    <div class="feature-item" [class.available]="vehicleData.smart_key">
                      <i class="ki-duotone ki-security-user fs-2 me-2" [class.text-success]="vehicleData.smart_key"
                        [class.text-muted]="!vehicleData.smart_key">
                        <span class="path1"></span>
                        <span class="path2"></span>
                      </i>
                      <span>Smart Key</span>
                    </div>
                    <div class="feature-item" [class.available]="vehicleData.backcamera">
                      <i class="ki-duotone ki-camera fs-2 me-2" [class.text-success]="vehicleData.backcamera"
                        [class.text-muted]="!vehicleData.backcamera">
                        <span class="path1"></span>
                        <span class="path2"></span>
                      </i>
                      <span>Back Camera</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Options from parsed data -->
            <div class="row g-4 mt-2" *ngIf="getFeaturesList().length > 0">
              <div class="col-12">
                <div class="info-group">
                  <h6 class="fw-bold text-gray-800 mb-3">Additional Options</h6>
                  <div class="d-flex flex-wrap gap-2">
                    <span *ngFor="let feature of getFeaturesList()" class="badge badge-light-primary fs-7 fw-semibold">
                      {{ feature }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Inspection Tab -->
          <div *ngIf="activeTab === 'inspection'" class="tab-content">
            <div class="inspection-sheet-container">
              <div class="d-flex justify-content-between align-items-center mb-4">
                <h6 class="fw-bold text-gray-800 mb-0">Inspection Sheet</h6>
                <button type="button" class="btn btn-light-primary btn-sm" (click)="toggleInspectionSheet()">
                  <i class="ki-duotone ki-eye fs-3 me-1">
                    <span class="path1"></span>
                    <span class="path2"></span>
                    <span class="path3"></span>
                  </i>
                  {{ showInspectionSheet ? 'Hide' : 'Show' }} Full Sheet
                </button>
              </div>

              <!-- Inspection Sheet Preview -->
              <div class="inspection-preview bg-light p-4 rounded text-center">
                <i class="ki-duotone ki-document fs-3x text-primary mb-3">
                  <span class="path1"></span>
                  <span class="path2"></span>
                </i>
                <p class="text-muted mb-3">Detailed inspection sheet available</p>
                <p class="small text-gray-600">
                  This vehicle has been thoroughly inspected. Click "Show Full Sheet" to view the complete inspection
                  report with detailed diagrams and condition notes.
                </p>
              </div>

              <!-- Full Inspection Sheet (when toggled) -->
              <div *ngIf="showInspectionSheet" class="inspection-full-sheet mt-4 p-4 bg-white border rounded">
                <div class="text-center mb-4">
                  <h5 class="fw-bold">{{ vehicleData.company_name }} {{ vehicleData.name }} - Inspection Report</h5>
                  <p class="text-muted">Lot No: {{ vehicleData.lot_no }} | Chassis: {{ vehicleData.inventory_no }}</p>
                </div>

                <!-- Inspection Details Grid -->
                <div class="row g-4">
                  <div class="col-md-6">
                    <div class="inspection-section">
                      <h6 class="fw-bold text-primary mb-3">Exterior Condition</h6>
                      <div class="condition-grid">
                        <div class="condition-item">
                          <span class="label">Overall:</span>
                          <span class="badge badge-success">Good</span>
                        </div>
                        <div class="condition-item">
                          <span class="label">Paint:</span>
                          <span class="badge badge-warning">Fair</span>
                        </div>
                        <div class="condition-item">
                          <span class="label">Body:</span>
                          <span class="badge badge-success">Excellent</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="inspection-section">
                      <h6 class="fw-bold text-primary mb-3">Interior Condition</h6>
                      <div class="condition-grid">
                        <div class="condition-item">
                          <span class="label">Seats:</span>
                          <span class="badge badge-success">Good</span>
                        </div>
                        <div class="condition-item">
                          <span class="label">Dashboard:</span>
                          <span class="badge badge-success">Good</span>
                        </div>
                        <div class="condition-item">
                          <span class="label">Electronics:</span>
                          <span class="badge badge-success">Working</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Comments Section -->
                <div class="mt-4"
                  *ngIf="vehicleData.parsed_data[' I-auc  comments '] || vehicleData.one_line_description">
                  <h6 class="fw-bold text-primary mb-3">Inspector Comments</h6>
                  <div class="bg-light p-3 rounded">
                    <p class="mb-0 text-gray-700">
                      {{ vehicleData.parsed_data[' I-auc comments '] || vehicleData.one_line_description || 'Noadditional comments from inspector.' }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Column - Bid Section -->
    <div class="col-lg-4">

      <!-- Make Bid Card -->
      <div class="card">
        <div class="card-header border-0 pt-6">
          <div class="card-title">
            <h3 class="fw-bold text-gray-900 m-0">
              <i class="ki-duotone ki-dollar fs-2 text-primary me-2">
                <span class="path1"></span>
                <span class="path2"></span>
                <span class="path3"></span>
              </i>
              {{ isAucnetOrOnePrice() ? 'Add to Stock' : 'Make a Bid' }}
            </h3>
          </div>
        </div>

        <div class="card-body">
          <!-- Current Price Display -->
          <div class="price-display mb-6">
            <div class="d-flex justify-content-between align-items-center p-4 bg-light-primary rounded">
              <div>
                <div class="text-muted small">Current Price</div>
                <div class="fs-2 fw-bold text-primary">¥{{ formatPrice(vehicleData.price) }}</div>
              </div>
              <div class="text-end">
                <div class="text-muted small">Status</div>
                <div class="badge" [class]="vehicleData.is_sold ? 'badge-danger' : 'badge-success'">
                  {{ vehicleData.is_sold ? 'SOLD' : 'AVAILABLE' }}
                </div>
              </div>
            </div>
          </div>

          <!-- For Aucnet or One-Price: Show Add to Real Stock Button -->
          <ng-container *ngIf="isAucnetOrOnePrice()">
            <!-- Show action buttons if vehicle is not added to stock -->
            <ng-container *ngIf="!vehicleData.is_added_to_stock || !vehicleData.vehicle_id">
              <button
                class="btn btn-lg w-100 btn-primary"
                (click)="addToRealStock()"
                [disabled]="isAddingToStock">
                <span *ngIf="isAddingToStock" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {{ isAddingToStock ? 'Adding...' : 'Add To Real Stock' }}
              </button>
            </ng-container>

            <!-- Show view stock link if vehicle is already added to stock -->
            <ng-container *ngIf="vehicleData.is_added_to_stock && vehicleData.vehicle_id">
              <div class="text-center p-4">
                <i class="ki-duotone ki-check-circle fs-3x text-success mb-3">
                  <span class="path1"></span>
                  <span class="path2"></span>
                </i>
                <h5 class="fw-bold text-success mb-3">Already Added to Stock</h5>
                <a
                  [routerLink]="['/vehicles/vehicle-manager/edit', vehicleData.vehicle_id]"
                  class="btn btn-light-success">
                  <i class="ki-duotone ki-eye fs-6 me-1">
                    <span class="path1"></span>
                    <span class="path2"></span>
                    <span class="path3"></span>
                  </i>
                  View the Stock Here
                </a>
              </div>
            </ng-container>
          </ng-container>

          <!-- For Alaedo: Show Bid Form -->
          <ng-container *ngIf="isAlaedo()">
            <!-- Minimum Bid Notice -->
            <div class="alert alert-info mb-4" *ngIf="!vehicleData.is_sold">
              <i class="ki-duotone ki-information-5 fs-2 text-info me-2">
                <span class="path1"></span>
                <span class="path2"></span>
                <span class="path3"></span>
              </i>
              <strong>Minimum Bid Amount should be {{ formatPrice(vehicleData.service_data?.minPrice || 0)}} JPY</strong>
            </div>

            <!-- Bid Form -->
            <form [formGroup]="bidForm" (ngSubmit)="onSubmitBid()" *ngIf="!vehicleData.is_sold">

            <!-- Bid Amount with Currency -->
            <div class="fv-row mb-4">
              <label class="form-label fw-semibold text-gray-800 required">Bid Amount</label>
              <div class="input-group input-group-lg">
                <input type="number"
                       class="form-control"
                       [class.is-invalid]="isFieldInvalid('bidAmount')"
                       formControlName="bidAmount"
                       placeholder="Enter bid amount">
                <select class="form-select"
                        style="max-width: 100px;"
                        formControlName="currency">
                  <option value="JPY">JPY</option>
                  <option value="USD">USD</option>
                </select>
              </div>
              <div *ngIf="isFieldInvalid('bidAmount')" class="invalid-feedback">
                {{ getFieldError('bidAmount') }}
              </div>
            </div>

            <!-- Auction Price Dropdown with Show C&F Price Button -->
            <div class="fv-row mb-4">
              <label class="form-label fw-semibold text-gray-800 required">Bid Type</label>
              <div class="d-flex gap-2">
                <select class="form-select form-select-lg flex-grow-1"
                        formControlName="bidType">
                  <option value="order">Order</option>
                  <option value="stock">Stock</option>
                </select>
                <!-- <button type="button"
                        class="btn btn-outline-primary btn-lg"
                        (click)="showCFPrice()">
                  Show C&F Price
                </button> -->
              </div>
            </div>

            <!-- Price Type Dropdown -->
            <div class="fv-row mb-4">
              <label class="form-label fw-semibold text-gray-800 required">Price Type</label>
              <select class="form-select form-select-lg"
                      [class.is-invalid]="isFieldInvalid('priceType')"
                      formControlName="priceType">
                <option value="">Select Price Type</option>
                <option value="C&F">C&F</option>
                <option value="FOB">FOB</option>
              </select>
              <div *ngIf="isFieldInvalid('priceType')" class="invalid-feedback">
                {{ getFieldError('priceType') }}
              </div>
            </div>

            <!-- Price Type Amount -->
            <div class="fv-row mb-4">
              <label class="form-label fw-semibold text-gray-800 required">Price Type Amount</label>
              <input type="number"
                     class="form-control form-control-lg"
                     [class.is-invalid]="isFieldInvalid('priceTypeAmount')"
                     formControlName="priceTypeAmount"
                     placeholder="Enter price type amount"
                     min="0">
              <div *ngIf="isFieldInvalid('priceTypeAmount')" class="invalid-feedback">
                {{ getFieldError('priceTypeAmount') }}
              </div>
            </div>

            <!-- Client Search -->
            <div class="fv-row mb-4">
              <label class="form-label fw-semibold text-gray-800">Client</label>
              <div class="position-relative">
                <input type="text"
                       class="form-control form-control-lg"
                       placeholder="Search Client..."
                       [(ngModel)]="clientSearchTerm"
                       [ngModelOptions]="{standalone: true}"
                       [disabled]="isClientFieldDisabled"
                       (input)="onClientSearchInput($event)"
                       (focus)="onClientInputFocus()"
                       (blur)="onClientInputBlur()"
                       autocomplete="off" />

                <!-- Client Search Results Dropdown -->
                <div class="dropdown-menu show w-100 mh-300px overflow-auto"
                     *ngIf="showClientDropdown && !isClientFieldDisabled && (clientSearchResults.length > 0 || isClientSearching)">

                  <!-- Loading State -->
                  <div class="dropdown-item text-center py-3" *ngIf="isClientSearching">
                    <span class="spinner-border spinner-border-sm me-2"></span>
                    Searching clients...
                  </div>

                  <!-- Search Results -->
                  <a class="dropdown-item cursor-pointer py-2"
                     *ngFor="let client of clientSearchResults"
                     (click)="selectClient(client)">
                    <div class="d-flex flex-column">
                      <span class="fw-bold">{{ getClientDisplayName(client) }}</span>
                      <small class="text-muted">{{ client.email }}</small>
                    </div>
                  </a>

                  <!-- No Results -->
                  <div class="dropdown-item text-center py-3 text-muted"
                       *ngIf="!isClientSearching && clientSearchResults.length === 0">
                    No clients found
                  </div>
                </div>
              </div>
            </div>

            <!-- Destination Country -->
            <div class="fv-row mb-4">
              <label class="form-label fw-semibold text-gray-800">Destination Country</label>
              <select class="form-select form-select-lg"
                      formControlName="destinationCountry">
                <option value="">Select Destination Country</option>
                <option *ngFor="let country of countries" [value]="country.code">
                  {{ country.name }}
                </option>
              </select>
            </div>

            <!-- Destination Port -->
            <div class="fv-row mb-4">
              <label class="form-label fw-semibold text-gray-800">Destination Port</label>
              <div class="position-relative">
                <select class="form-select form-select-lg"
                        formControlName="destinationPort"
                        [disabled]="!bidForm.get('destinationCountry')?.value || isLoadingPorts">
                  <option value="">Select Destination Port</option>
                  <option *ngFor="let port of ports" [value]="port._id">
                    {{ port.name }}
                  </option>
                </select>
                <div *ngIf="isLoadingPorts" class="position-absolute top-50 end-0 translate-middle-y me-3">
                  <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
                </div>
              </div>
            </div>

            <!-- C&F Price -->
            <div class="fv-row mb-4" *ngIf="bidForm.get('priceType')?.value === 'C&F'">
              <label class="form-label fw-semibold text-gray-800">C&F Price</label>
              <div class="position-relative">
                <input type="text"
                       class="form-control form-control-lg"
                       [value]="cfPrice !== null ? cfPrice : ''"
                       placeholder="Select port to load C&F price"
                       disabled
                       readonly>
                <div *ngIf="isLoadingCFPrice" class="position-absolute top-50 end-0 translate-middle-y me-3">
                  <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
                </div>
              </div>
            </div>

            <!-- Group Dropdown -->
            <div class="fv-row mb-4">
              <label class="form-label fw-semibold text-gray-800 required">Group</label>
              <select class="form-select form-select-lg" [class.is-invalid]="isFieldInvalid('group')"
                formControlName="group">
                <option value="">Select Group</option>
                <option value="A">Group A</option>
                <option value="B">Group B</option>
                <option value="C">Group C</option>
                <option value="D">Group D</option>
              </select>
              <div *ngIf="isFieldInvalid('group')" class="invalid-feedback">
                {{ getFieldError('group') }}
              </div>
            </div>

            <!-- Number of Units -->
            <div class="fv-row mb-4">
              <label class="form-label fw-semibold text-gray-800">Number of Units</label>
              <div class="position-relative">
                <input type="number"
                       class="form-control form-control-lg"
                       [class.pe-5]="showEditIcon"
                       formControlName="numberOfUnits"
                       placeholder="Select group to load units">
                <div *ngIf="isLoadingBidUnit" class="position-absolute top-50 end-0 translate-middle-y me-3">
                  <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
                </div>
                <button *ngIf="showEditIcon && !isLoadingBidUnit"
                        type="button"
                        class="btn btn-icon btn-sm btn-light-primary position-absolute top-50 end-0 translate-middle-y me-2"
                        (click)="openUpdateBidUnitModal()"
                        title="Edit Bid Unit">
                  <i class="ki-duotone ki-pencil fs-3">
                    <span class="path1"></span>
                    <span class="path2"></span>
                  </i>
                </button>
              </div>
            </div>

            <!-- Message -->
            <div class="fv-row mb-6">
              <label class="form-label fw-semibold text-gray-800">Message (0 / 1000)</label>
              <textarea class="form-control"
                        formControlName="message"
                        rows="4"
                        maxlength="1000"
                        placeholder="Enter your message here..."></textarea>
              <div class="form-text">{{ bidForm.get('message')?.value?.length || 0 }} / 1000 characters</div>
            </div>

            <!-- Submit Button -->
            <button type="submit"
                    class="btn btn-lg w-100"
                    style="background-color: var(--bs-primary); border-color: var(--bs-primary); color: white;"
                    [disabled]="bidForm.invalid || isSubmittingBid">
              <span *ngIf="!isSubmittingBid">Make Bid</span>
              <span *ngIf="isSubmittingBid">
                <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                Submitting...
              </span>
            </button>

          </form>

          <!-- Sold Message -->
          <div *ngIf="vehicleData.is_sold" class="text-center p-4">
            <i class="ki-duotone ki-cross-circle fs-3x text-danger mb-3">
              <span class="path1"></span>
              <span class="path2"></span>
            </i>
            <h5 class="fw-bold text-danger mb-2">Vehicle Sold</h5>
            <p class="text-muted mb-0">This vehicle has already been sold and is no longer available for bidding.</p>
          </div>
          </ng-container>

        </div>
      </div>

      <!-- Send an Inquiry Card -->
      <div class="card mt-4">
        <div class="card-header border-0 pt-6">
          <div class="card-title">
            <h3 class="fw-bold text-gray-900 m-0">
              <i class="ki-duotone ki-message-text-2 fs-2 text-primary me-2">
                <span class="path1"></span>
                <span class="path2"></span>
                <span class="path3"></span>
              </i>
              Send an Inquiry
            </h3>
          </div>
        </div>

        <div class="card-body">
          <!-- Inquiry Form -->
          <form [formGroup]="inquiryForm" (ngSubmit)="onSubmitInquiry()">

            <!-- Message Textarea -->
            <div class="fv-row mb-4">
              <label class="form-label fw-semibold text-gray-800 required">Your Message</label>
              <textarea class="form-control"
                        [class.is-invalid]="isInquiryFieldInvalid('inquiryMessage')"
                        formControlName="inquiryMessage"
                        rows="4"
                        maxlength="1000"
                        placeholder="Enter your inquiry message here..."></textarea>
              <div *ngIf="isInquiryFieldInvalid('inquiryMessage')" class="invalid-feedback">
                {{ getInquiryFieldError('inquiryMessage') }}
              </div>
              <div class="form-text">{{ inquiryForm.get('inquiryMessage')?.value?.length || 0 }} / 1000 characters</div>
            </div>

            <!-- Submit Button -->
            <button type="submit"
                    class="btn btn-lg w-100 btn-light-primary"
                    [disabled]="inquiryForm.invalid || isSubmittingInquiry || !inquiryForm.get('inquiryMessage')?.value?.trim()">
              <span *ngIf="!isSubmittingInquiry">Send Inquiry</span>
              <span *ngIf="isSubmittingInquiry">
                <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                Sending...
              </span>
            </button>

          </form>
        </div>
      </div>

      <!-- Quick Info Card -->


    </div>

  </div>

</div>

<!-- Error State -->
<div *ngIf="!isLoading && !vehicleData" class="d-flex flex-column justify-content-center align-items-center"
  style="min-height: 400px;">
  <i class="ki-duotone ki-cross-circle fs-3x text-danger mb-4">
    <span class="path1"></span>
    <span class="path2"></span>
  </i>
  <h4 class="fw-bold text-gray-900 mb-2">Vehicle Not Found</h4>
  <p class="text-muted mb-4">The requested vehicle could not be found or may have been removed.</p>
  <button type="button" class="btn btn-primary" (click)="goBack()">
    <i class="ki-duotone ki-arrow-left fs-2 me-2">
      <span class="path1"></span>
      <span class="path2"></span>
    </i>
    Back to Search
  </button>
</div>

<!-- Update Bid Unit Modal -->
<app-update-bid-unit (bidUnitUpdated)="onBidUnitUpdated()"></app-update-bid-unit>
